// Device-Drawer Aktionen-Bereich (TQ #5266 Phase 2 Iteration 1).
//
// User-initiierte Commands fuer ein Geraet:
//   - Service neustarten (Dropdown aus capabilities.services)
//   - System-Updates (Confirm: kann Reboots ausloesen)
//   - Backup pruefen
//
// Senden ueber POST /api/org/<slug>/devices/<id>/commands.
// Polling-Status via CommandStatus (Phase 2 Iter 1, WS spaeter).

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { CommandStatus } from './CommandStatus'

export interface DeviceLike {
  device_id: string
  hostname: string
  device_type: string
  status?: string
  capabilities?: Record<string, unknown>
}

interface DeviceActionsPanelProps {
  orgSlug: string
  device: DeviceLike
  isOperator: boolean
  /** Org-Owner/Admin — schaltet die Gefahrenzone (Stilllegen/Löschen) frei,
   *  unabhängig vom isOperator-Gate (das für decommissioned-Geräte false ist). */
  isAdmin: boolean
  /** Nach Lifecycle-Änderung: removed=true bei Hard-Purge (Drawer schließen),
   *  false bei Stilllegen (Liste neu laden, Drawer offen lassen). */
  onLifecycleChange?: (removed: boolean) => void
}

interface QueueCommandResponse {
  command_id: string
  status: string // 'pending' | 'running' | 'done' | 'failed'
}

interface CommandRequest {
  type: 'restart_service' | 'update_packages' | 'check_backup'
  service_name?: string
}

interface ConfirmRequest {
  action: 'restart_service' | 'update_packages' | 'check_backup'
  service_name?: string
  label: string
  consequence: string
}

/** Extrahiert die Service-Liste aus device.capabilities. Akzeptiert
 *  sowohl Array<string> als auch Array<{name: string}> — der Agent
 *  liefert Strings, aber zukuenftige Capabilities-Erweiterungen
 *  koennten Objekte enthalten. */
function extractServiceNames(capabilities?: Record<string, unknown>): string[] {
  const raw = capabilities?.services
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const entry of raw) {
    if (typeof entry === 'string') {
      out.push(entry)
    } else if (entry && typeof entry === 'object' && 'name' in entry) {
      const name = (entry as { name?: unknown }).name
      if (typeof name === 'string') out.push(name)
    }
  }
  return out.sort()
}

export function DeviceActionsPanel({
  orgSlug,
  device,
  isOperator,
  isAdmin,
  onLifecycleChange,
}: DeviceActionsPanelProps) {
  const { api } = useAuth()
  const [selectedService, setSelectedService] = useState('')
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmRequest | null>(null)
  const [activeCommandId, setActiveCommandId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Gefahrenzone (TQ #7855): 'revoke' = Soft-Delete (status=decommissioned),
  // 'purge' = Hard-Purge (permanente Entfernung, Backend HardPurgeOrgDevice #7849).
  const [dangerKind, setDangerKind] = useState<'revoke' | 'purge' | null>(null)
  const isDecommissioned = device.status === 'decommissioned'

  const performDanger = useCallback(async () => {
    if (!api || !dangerKind) return
    setSubmitting(true)
    setError(null)
    const removed = dangerKind === 'purge'
    const url = removed
      ? `/api/org/${orgSlug}/devices/${device.device_id}/purge`
      : `/api/org/${orgSlug}/devices/${device.device_id}`
    try {
      await api.delete(url)
      setDangerKind(null)
      onLifecycleChange?.(removed)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      setError(err.response?.data?.error ?? err.message ?? 'Aktion fehlgeschlagen.')
    } finally {
      setSubmitting(false)
    }
  }, [api, dangerKind, orgSlug, device.device_id, onLifecycleChange])

  const services = useMemo(
    () => extractServiceNames(device.capabilities),
    [device.capabilities],
  )

  const queueCommand = useCallback(
    async (req: CommandRequest) => {
      if (!api) return
      setSubmitting(true)
      setError(null)
      try {
        const r = await api.post<QueueCommandResponse>(
          `/api/org/${orgSlug}/devices/${device.device_id}/commands`,
          req,
        )
        if (r.data?.command_id) {
          setActiveCommandId(r.data.command_id)
        } else {
          setError('Backend lieferte keine command_id zurück.')
        }
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } }; message?: string }
        setError(
          err.response?.data?.error ?? err.message ?? 'Command konnte nicht angelegt werden.',
        )
      } finally {
        setSubmitting(false)
      }
    },
    [api, orgSlug, device.device_id],
  )

  const onConfirm = useCallback(async () => {
    if (!pendingConfirm) return
    const req: CommandRequest =
      pendingConfirm.action === 'restart_service'
        ? { type: 'restart_service', service_name: pendingConfirm.service_name }
        : pendingConfirm.action === 'update_packages'
          ? { type: 'update_packages' }
          : { type: 'check_backup' }
    setPendingConfirm(null)
    await queueCommand(req)
  }, [pendingConfirm, queueCommand])

  return (
    <div style={{ marginTop: '1.5rem' }}>
      {isOperator ? (
        <>
          <h3
            style={{
              margin: '0 0 0.75rem',
              fontSize: '0.95rem',
              color: '#1f2937',
              fontWeight: 600,
            }}
          >
            Aktionen
          </h3>

          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {/* Restart Service */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                disabled={services.length === 0 || submitting}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem',
                  background: services.length === 0 ? '#f9fafb' : '#fff',
                }}
              >
                <option value="">
                  {services.length === 0
                    ? 'Keine Services in den Capabilities gemeldet'
                    : 'Service wählen…'}
                </option>
                {services.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button
                onClick={() =>
                  setPendingConfirm({
                    action: 'restart_service',
                    service_name: selectedService,
                    label: `Service neustarten: ${selectedService}`,
                    consequence: `systemctl restart ${selectedService} läuft auf ${device.hostname}. Kurze Service-Unterbrechung möglich.`,
                  })
                }
                disabled={!selectedService || submitting}
                style={btnStyle(!selectedService || submitting)}
              >
                Neustarten
              </button>
            </div>

            {/* Update Packages */}
            <button
              onClick={() =>
                setPendingConfirm({
                  action: 'update_packages',
                  label: 'System-Updates installieren',
                  consequence: `apt/dnf/zypper/winget upgrade auf ${device.hostname}. Kann Kernel-Update + Reboot-Bedarf nach sich ziehen.`,
                })
              }
              disabled={submitting}
              style={btnStyle(submitting)}
            >
              System-Updates
            </button>

            {/* Check Backup */}
            <button
              onClick={() =>
                setPendingConfirm({
                  action: 'check_backup',
                  label: 'Backup-Status prüfen',
                  consequence: `restic snapshots --json (oder PBS-Sync) auf ${device.hostname}. Read-only, keine Aenderungen.`,
                })
              }
              disabled={submitting}
              style={btnStyle(submitting)}
            >
              Backup prüfen
            </button>
          </div>

          {activeCommandId && (
            <CommandStatus
              orgSlug={orgSlug}
              deviceId={device.device_id}
              commandId={activeCommandId}
              onDone={() => {
                // Lasse das Result sichtbar, der User dismissed via X
              }}
            />
          )}
        </>
      ) : (
        <div
          style={{
            padding: '0.75rem',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            fontSize: '0.8125rem',
            color: '#6b7280',
          }}
        >
          {isDecommissioned
            ? 'Gerät ist stillgelegt — keine Befehle möglich.'
            : 'Geräte-Aktionen sind nur für Org-Owner/Operator verfügbar.'}
        </div>
      )}

      {/* Gefahrenzone (TQ #7855): Stilllegen (soft) + Endgültig löschen (hard-purge). */}
      {isAdmin && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '0.75rem',
            border: '1px solid #fecaca',
            borderRadius: 8,
            background: '#fff5f5',
          }}
        >
          <h3
            style={{
              margin: '0 0 0.5rem',
              fontSize: '0.95rem',
              color: '#991b1b',
              fontWeight: 600,
            }}
          >
            Gefahrenzone
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {!isDecommissioned && (
              <button
                onClick={() => setDangerKind('revoke')}
                disabled={submitting}
                style={dangerBtnStyle(submitting)}
              >
                Gerät stilllegen (Zugriff sofort sperren)
              </button>
            )}
            <button
              onClick={() => setDangerKind('purge')}
              disabled={submitting}
              style={dangerBtnStyle(submitting)}
            >
              Gerät endgültig löschen
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            borderRadius: 6,
            fontSize: '0.8125rem',
          }}
        >
          {error}
        </div>
      )}

      {pendingConfirm && (
        <ConfirmModal
          title={pendingConfirm.label}
          consequence={pendingConfirm.consequence}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={onConfirm}
        />
      )}

      {dangerKind && (
        <ConfirmModal
          title={dangerKind === 'purge' ? 'Gerät endgültig löschen' : 'Gerät stilllegen'}
          consequence={
            dangerKind === 'purge'
              ? `${device.hostname} wird PERMANENT entfernt (inkl. Freigaben + Metriken). Nicht umkehrbar. Ein noch laufender Agent wird beim nächsten Kontakt mit 403 abgewiesen und deinstalliert sich selbst.`
              : `${device.hostname} wird stillgelegt (decommissioned). Der Agent wird ab sofort server-seitig mit 403 abgewiesen. Der Datensatz bleibt erhalten — endgültiges Löschen separat.`
          }
          confirmLabel={dangerKind === 'purge' ? 'Endgültig löschen' : 'Stilllegen'}
          onCancel={() => setDangerKind(null)}
          onConfirm={performDanger}
        />
      )}
    </div>
  )
}

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '0.5rem 0.875rem',
    background: disabled ? '#e5e7eb' : '#2563eb',
    color: disabled ? '#9ca3af' : '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  }
}

function dangerBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '0.5rem 0.875rem',
    background: disabled ? '#e5e7eb' : '#dc2626',
    color: disabled ? '#9ca3af' : '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.875rem',
    fontWeight: 600,
    textAlign: 'left',
  }
}

function ConfirmModal({
  title,
  consequence,
  confirmLabel = 'Ja, ausführen',
  onCancel,
  onConfirm,
}: {
  title: string
  consequence: string
  confirmLabel?: string
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '1.5rem',
          minWidth: 380,
          maxWidth: 520,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.05rem' }}>
          {title}
        </h3>
        <p style={{ margin: '0 0 1.25rem', color: '#374151', fontSize: '0.875rem' }}>
          {consequence}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem',
              background: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '0.5rem 1rem',
              background: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
